/**
 * Robust optimistic writes for Project.state entity lists.
 *
 * Two Apollo v4 traps make the naive readQuery→writeQuery optimistic pattern
 * unreliable (see project memory "Apollo v4 optimistic-write gotchas"):
 *  1. a strict full-query readQuery returns NULL (no throw) when ANY selected
 *     field is missing anywhere in the cache — silently skipping the write;
 *  2. raw cache-level writes (cache.modify / cache.writeFragment) only fire
 *     cache watches; ACTIVE QUERIES (the panel's useQuery) are notified via
 *     the CLIENT's query broadcast.
 *
 * Pattern: write entities via cache.writeFragment, append refs to the state
 * lists via cache.modify (both tolerate incomplete caches), then have the
 * caller finish with ONE client-level write (apolloClient.writeFragment) to
 * trigger the query broadcast with everything already in place.
 */

import gql from 'graphql-tag';
import type { Reference, StoreObject } from '@apollo/client';
import type { useApolloClient } from '@apollo/client/react';

type Cache = ReturnType<typeof useApolloClient>['cache'];

export const ORDERED_ENTITY_FRAGMENT = gql`
  fragment OrderedEntity on orderedIdentifier {
    identifier
    index
  }
`;

const ORDERED_INDEX_FRAGMENT = gql`
  fragment ProjectOrderedIndices on Project {
    state {
      orderedIdentifiers {
        identifier
        index
      }
    }
  }
`;

/** Cache-layer shape of Project.state — entity lists are normalized refs. */
interface StateStoreObject extends StoreObject {
  annotations?: Reference[];
  groups?: Reference[];
  orderedIdentifiers?: Reference[];
}

/** The Project entity's cache id, or null with a logged error (entity gone). */
export function identifyProject(cache: Cache, projectId: string): string | null {
  const ref = cache.identify({ __typename: 'Project', id: projectId });
  if (!ref) console.error('[optimisticProjectState] project entity missing from cache — optimistic write skipped');
  return ref ?? null;
}

/** Next free orderedIdentifiers index (max + 1). */
export function nextOrderedIndex(cache: Cache, projectRef: string): number {
  const data = cache.readFragment<{ state?: { orderedIdentifiers?: { index: number }[] } | null }>({
    id: projectRef,
    fragment: ORDERED_INDEX_FRAGMENT,
  });
  return (data?.state?.orderedIdentifiers ?? []).reduce((max, o) => Math.max(max, o.index), -1) + 1;
}

/**
 * Append entity refs to Project.state's lists, deduped by `identifier` (a
 * retried optimistic write must not stack a second copy). Cache-level only —
 * the caller must follow up with a client-level write to notify queries.
 */
export function appendToProjectState(
  cache: Cache,
  projectRef: string,
  refs: { identifier: string; annotation?: Reference | undefined; group?: Reference | undefined; ordered?: Reference | undefined },
): void {
  cache.modify({
    id: projectRef,
    fields: {
      state(existing, { readField, isReference }) {
        if (isReference(existing)) return existing; // state is embedded, never a ref
        const state = existing as StateStoreObject;
        const hasId = (list: Reference[] | undefined): boolean => (list ?? []).some((ref) => readField<string>('identifier', ref) === refs.identifier);
        if (hasId(state.annotations) || hasId(state.groups)) return existing;
        return {
          ...state,
          annotations: refs.annotation ? [...(state.annotations ?? []), refs.annotation] : state.annotations,
          groups: refs.group ? [...(state.groups ?? []), refs.group] : state.groups,
          orderedIdentifiers: refs.ordered && !hasId(state.orderedIdentifiers) ? [...(state.orderedIdentifiers ?? []), refs.ordered] : state.orderedIdentifiers,
        };
      },
    },
  });
}

const GROUP_TYPENAMES_FRAGMENT = gql`
  fragment ProjectGroupTypenames on Project {
    state {
      groups {
        __typename
        identifier
      }
    }
  }
`;

/** The concrete __typename of a group already in the cache, or null. */
export function groupTypenameInCache(cache: Cache, projectRef: string, identifier: string): string | null {
  const data = cache.readFragment<{ state?: { groups?: { __typename: string; identifier: string }[] } | null }>({
    id: projectRef,
    fragment: GROUP_TYPENAMES_FRAGMENT,
  });
  return data?.state?.groups?.find((g) => g.identifier === identifier)?.__typename ?? null;
}
